import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Box, Flex, Link, Spinner } from "theme-ui";
import moment from "moment-timezone";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { BackArrowIcon, QuestionMarkIcon } from "components/svg";
import { themevals } from "theme/themevals";
import { mnt, mntCard, mntType } from "theme/maintenanceTokens";
import { useToast } from "components/toast";
import UserContext from "context/User";

import {
  getServicePlan,
  closeServicePlan,
  getPlanStats,
  getPlanEnrichedSites,
  getPlanLocationMap,
  getPlanActivity,
  getPlanGroups,
  getWorkOrders,
  addToGroupRoster,
  removeFromGroupRoster,
  getInstantEstimateProducts,
} from "api/ServicePlans";
import ImportSitesModal from "../Clients/components/ImportSitesModal";

import PlanStatCards, { PlanStats } from "./components/PlanStatCards";
import PlanFinancialCards from "./components/PlanFinancialCards";
import PlanSectionHeader from "./components/PlanSectionHeader";
import WorkOrderGroupsSection, {
  WorkOrderGroup,
} from "./components/WorkOrderGroupsSection";
import PlanWorkOrdersSection from "./components/PlanWorkOrdersSection";
import PlanLocationsTable from "./components/PlanLocationsTable";
import PlanLocationsMap, {
  PlanMapPoint,
  PlanMapStatus,
} from "./components/PlanLocationsMap";
import type { EnrichedSite } from "./components/planRosterTypes";
import PlanConfigPanel from "./components/PlanConfigPanel";
import RecentActivityPanel, { ActivityEvent } from "./components/RecentActivityPanel";
import RecurringWorkPanel from "./components/RecurringWorkPanel";
import EditPlanModal from "./components/EditPlanModal";
import AddLocationToPlanModal from "./components/AddLocationToPlanModal";
import NewWorkOrderModal from "./components/NewWorkOrderModal";
import WorkOrderImportModal from "components/WorkOrderImportModal";
import NewDirectQuoteModal from "./components/NewDirectQuoteModal";
import AttachExistingWorkModal from "./components/AttachExistingWorkModal";
import MatchCandidatesModal from "./components/MatchCandidatesModal";
import PlanClientDetail from "./PlanClientDetail";

/**
 * MASTER_PLAN.md §3.9 #22 (Session 41 refresh) — full Plan Detail page
 * matching mockup 13 end-to-end.
 *
 * SESSION 46 layout pass (ALIGNMENT_REVIEW Misalignment 4 + Jack's
 * sandbox screenshot). The mockup is a CENTERED composition — one
 * bounded content block with the rail hugging its right edge. Shipped
 * layout let the header buttons fly to the viewport's right edge while
 * the capped content hugged the left, leaving a dead gutter on wide
 * screens. Everything (header row included) now lives inside one
 * centered max-width container, the main column flexes to fill it, and
 * the rail is a fixed-width right edge instead of a free-floating
 * island.
 *
 * Layout: two columns. Main column carries header + stat cards +
 * batches + enriched sites list. Right rail carries plan config
 * summary + recent activity feed. On narrow viewports the rail stacks
 * below the sites list.
 *
 * Manage-side: Edit plan modal opens from the config-panel Edit link
 * AND the header Edit button (two entry points, same modal). Import
 * Sites and Close Plan stay on the header. Create Batch lives inside
 * WorkOrderGroupsSection so an admin never leaves the page to
 * configure batches.
 *
 * Client-side viewers (non-isCompany) see the same page with
 * management affordances hidden — Edit, Import Sites, Close Plan,
 * New Batch, and the config-panel Edit link all disappear. This
 * matches §3.5's "read-only mirror of admin" rule; the standalone
 * client-facing PlansTab (#21) still exists on /clients/:id for the
 * curated per-client view.
 *
 * Session 46: batch allocation and per-Location work creation moved
 * into the Locations table (PlanLocationsTable — formerly EnrichedSitesList,
 * retired). Allocation uses the
 * table's "Select to batch" multi-select + `onAllocate`; raising a WO
 * or issuing a quote for one Location happens from the row's inline
 * expand via `onNewWorkOrder` / `onNewQuote`. `refreshAfterRosterChange`
 * hands a spinner-free page reload down so the Batch column, batch
 * card stats, and the activity feed stay truthful after an allocation
 * without collapsing the table the user is working in.
 */

/**
 * Route entry for `/maintenance/plans/:id`. Role-branches like Projects:
 * staff get the operator plan console below; a client gets the lean
 * read-only plan view (`PlanClientDetail`, mockup 10). The operator
 * component — with its batches, roster, locations map, config panel and
 * plan-WO fetches — is never mounted for a client, so none of those
 * operator reads fire for a client token. The plan read endpoints are
 * already client-safe (server-scoped, pricing-stripped) either way.
 *
 * This supersedes the earlier "same page with management affordances
 * hidden" approach: at 1,084-site volume the operator console (batches,
 * roster, map) is not a plan the client should scroll, so the client
 * gets the curated view instead of the staff surface with buttons
 * removed.
 */
export default function PlanDetail() {
  const { user } = React.useContext(UserContext) as any;
  if (!user) return null;
  if (user.accessLevel !== "isCompany") return <PlanClientDetail />;
  return <OperatorPlanDetail />;
}

function OperatorPlanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = React.useContext(UserContext) as any;

  const isAdmin = user?.role !== null && user?.accessLevel === "isCompany";

  const [plan, setPlan] = useState<any>(null);
  const [stats, setStats] = useState<PlanStats | null>(null);
  const [enrichedSites, setEnrichedSites] = useState<EnrichedSite[]>([]);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [groups, setGroups] = useState<WorkOrderGroup[]>([]);
  const [loading, setLoading] = useState(true);

  // Session 48 — the plan's Work Orders live here so the plan-scoped WO
  // section AND the per-Location row expansion in the Locations table
  // read from ONE source. The section drives the lifecycle filter; the
  // row expansion shows this location's slice regardless of that filter
  // (it fetches its own "all" slice so a row always shows every open WO
  // even when the section is filtered to, say, needs-convert).
  const [planWorkOrders, setPlanWorkOrders] = useState<any[] | null>(null);
  const [planWosLoading, setPlanWosLoading] = useState(true);
  const [woLifecycle, setWoLifecycle] = useState<string>("open");
  // Separate unfiltered slice for the row expansions, so a filtered
  // section never hides a Location's open work in its own row.
  const [lcpWorkOrders, setLcpWorkOrders] = useState<any[]>([]);
  // Bumped after any write that can move a roster row, so the paged
  // Locations table refetches its current page (it owns its own query
  // state and cannot be refreshed by handing it new props).
  const [rosterVersion, setRosterVersion] = useState(0);

  /**
   * P-i / P-l (RC5, mockup 28) — ONE SCOPE for the page. Selecting a
   * batch narrows the roster and the work queue together instead of
   * each section carrying its own private filter. Null = whole plan.
   */
  const [scopeGroupId, setScopeGroupId] = useState<string | null>(null);

  // P-m — the roster's saved view, lifted so the triage line can scope
  // the roster (e.g. to "overdue, no WO raised") from outside it.
  const [rosterView, setRosterView] = useState<string>("all");
  // Scroll targets for the triage links: mockup 28 P-m says each figure
  // scopes the page below AND brings you to it.
  const woSectionRef = React.useRef<HTMLDivElement>(null);
  const rosterSectionRef = React.useRef<HTMLDivElement>(null);

  /**
   * P-m — roster counts reported up by the Locations table, so the
   * triage line can state what needs attention without a second query.
   * The table already computes these against the whole plan.
   */
  const [rosterCounts, setRosterCounts] = useState<any>({});

  /**
   * TABS (Session 76, Jack's call).
   *
   * The page was a single vertical stack of ten sections and grew with
   * every location and work order added — 5,364px on production LCG.
   * Ordering the stack better (earlier in this session) made it shorter,
   * not smaller: every section still rendered on every visit.
   *
   * Tabs work here because the jobs are temporally separate. Pricing the
   * work orders, managing the roster and batches, checking margin, and
   * reading config are four different sittings. Nobody does two at once.
   *
   * What tabs cost is cross-tab signal — being on Sites and not knowing
   * 40 work orders need an RFQ. Two things pay that back:
   *   - the tab labels carry counts, so the number is visible from
   *     whichever tab you are on;
   *   - "Needs attention today" and Plan totals sit ABOVE the tab bar and
   *     never hide. Those are the things allowed to interrupt you.
   *
   * Held in the URL (?tab=) so a refresh, a back button and a shared link
   * all land where you were. Deep links from elsewhere in the app can
   * target a tab directly.
   */
  const initialTab = (() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["work", "sites", "financials", "reference"].includes(t || "")
      ? (t as PlanTab)
      : "work";
  })();
  const [tab, setTab] = useState<PlanTab>(initialTab);

  const selectTab = (next: PlanTab) => {
    setTab(next);
    const params = new URLSearchParams(window.location.search);
    if (next === "work") params.delete("tab");
    else params.set("tab", next);
    const qs = params.toString();
    // replace, not push: tab changes are not navigation, and stacking
    // them would make the back button walk through every tab you
    // glanced at instead of leaving the page.
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (qs ? `?${qs}` : ""),
    );
  };

  /**
   * N5 — plan locations map. Shown by default (Jack, Session 61) with a
   * Hide option; the geometry read is lazy (fires when the panel is open)
   * and cached in state so toggling closed/open doesn't refetch.
   * `mapStatusFilter` narrows the plotted pins client-side; the batch
   * scope reuses the page's `scopeGroupId` so the map agrees with the
   * batch chip already selected on the page.
   */
  // Session 76: the map has its own tab, so opening that tab IS the
  // request to see it — it renders expanded. The fetch stays gated on
  // this flag, and the map only mounts on its own tab, so no other tab
  // pays for it.
  const [showMap, setShowMap] = useState(true);
  const [mapPoints, setMapPoints] = useState<PlanMapPoint[] | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapStatusFilter, setMapStatusFilter] = useState<PlanMapStatus | "all" | "overdue">("all");
  const [selectedMapLocationId, setSelectedMapLocationId] = useState<string | null>(null);

  /**
   * P-k — the work order to show on each roster row, joined from the
   * plan's already-loaded WO list. When a location has several, the one
   * that NEEDS SOMETHING wins: an unmatched work order matters more to
   * an operator scanning the roster than a finished one sitting behind
   * it. Finished work (invoiced / declined / rejected) never wins, so a
   * location with old completed work and a fresh one shows the fresh one.
   */
  const woByLcp = useMemo(() => {
    const RANK: Record<string, number> = {
      needs_match: 0,
      intake: 1,
      rfq_generated: 2,
      quote_sent: 3,
      accepted: 4,
      in_production: 5,
      invoiced: 6,
      declined: 7,
    };
    const rankOf = (wo: any) => {
      if (wo?.status === "rejected") return 9;
      const stage = (wo?.effectiveStage as string) || "intake";
      if (stage === "intake" && wo?.matchStatus === "unmatched") return RANK.needs_match;
      return RANK[stage] ?? 8;
    };

    const map: Record<string, any> = {};
    for (const wo of lcpWorkOrders || []) {
      const lcpId = (wo as any)?.lcp?.id ?? null;
      if (!lcpId) continue;
      const current = map[lcpId];
      if (!current || rankOf(wo) < rankOf(current)) map[lcpId] = wo;
    }
    return map;
  }, [lcpWorkOrders]);

  // P-k — matching completes in a modal, so it can happen on the roster.
  const [rosterMatchTarget, setRosterMatchTarget] = useState<any>(null);

  /**
   * P-c — per-batch live work signal, derived from the plan's full WO
   * list (already loaded for the row expansions). Open = still needs a
   * WO-level action; past-SLA = complete-by has passed and the case is
   * not finished. Computed here rather than fetched because
   * `GET /groups/:id/stats` carries roster/progress counts only, and
   * because these two numbers are the ones that make a batch a cohort
   * you manage against rather than a label.
   */
  const woStatsByGroup = useMemo(() => {
    const map: Record<string, { open: number; overdue: number }> = {};
    const today = moment();
    for (const wo of lcpWorkOrders || []) {
      const gid = (wo as any)?.group?.id ?? (wo as any)?.groupId ?? null;
      if (!gid) continue;
      if (!map[gid]) map[gid] = { open: 0, overdue: 0 };

      if (wo?.status === "received" || wo?.status === "matched") map[gid].open++;

      // N4: finished = case-closed (every money leg settled) or legacy
      // rejected - NOT merely "stage is invoiced". An invoiced-but-open WO
      // past its complete-by is still overdue.
      const finished = wo?.status === "rejected" || (wo as any)?.caseClosed === true;
      if (!finished && wo?.completeByAt && moment(wo.completeByAt).isBefore(today, "day")) {
        map[gid].overdue++;
      }
    }
    return map;
  }, [lcpWorkOrders]);

  /**
   * P-j — per-batch stage distribution, from the same in-memory WO list.
   * Pre-RFQ work orders are split by whether they still need a site
   * match, because "needs match" and "needs RFQ" are different jobs for
   * different people; past that, the derived stage is used as-is.
   */
  const stageDistByGroup = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    for (const wo of lcpWorkOrders || []) {
      const gid = (wo as any)?.group?.id ?? (wo as any)?.groupId ?? null;
      if (!gid) continue;
      if (wo?.status === "rejected") continue;
      if (!map[gid]) map[gid] = {};

      const stage = (wo as any)?.effectiveStage as string | undefined;
      let key: string;
      if (!stage || stage === "intake") {
        key = wo?.matchStatus === "unmatched" ? "needs_match" : "rfq_generated";
      } else {
        key = stage;
      }
      map[gid][key] = (map[gid][key] ?? 0) + 1;
    }
    return map;
  }, [lcpWorkOrders]);

  /**
   * P-m — "what needs me today". Everything else on this page is a
   * browsable list; this is the only line that states the work. Every
   * figure comes from data the page already holds: the WO list for the
   * work-order figures, the roster's plan-wide counts for the location
   * ones.
   */
  const triage = useMemo(() => {
    let unmatched = 0;
    let pastSla = 0;
    const today = moment();

    for (const wo of lcpWorkOrders || []) {
      if (wo?.status === "rejected") continue;
      const open = wo?.status === "received" || wo?.status === "matched";
      if (open && wo?.matchStatus === "unmatched") unmatched++;
      // N4: finished = case-closed, not "stage is invoiced".
      const finished = (wo as any)?.caseClosed === true;
      if (!finished && wo?.completeByAt && moment(wo.completeByAt).isBefore(today, "day")) {
        pastSla++;
      }
    }

    // P-m — overdue locations with nothing raised against them. Exact,
    // computed in SQL by the roster endpoint. It used to be derived here
    // as (overdue - hasOpenWo), which is wrong whenever a site is both
    // overdue and carrying work, and could only be shown as "about N".
    // Falls back to the old subtraction when the roster has not reported
    // yet or the API predates the field, so the line degrades rather
    // than vanishing.
    const overdue = Number(rosterCounts?.overdue ?? 0);
    const hasOpenWo = Number(rosterCounts?.hasOpenWo ?? 0);
    const exactOverdueNoWo =
      rosterCounts?.overdueNoWo != null ? Number(rosterCounts.overdueNoWo) : null;
    const overdueNoWo = exactOverdueNoWo ?? Math.max(0, overdue - hasOpenWo);

    return {
      unmatched,
      pastSla,
      overdue,
      overdueNoWo,
      overdueNoWoExact: exactOverdueNoWo != null,
    };
  }, [lcpWorkOrders, rosterCounts]);

  /**
   * P-d — the date the commitment is actually judged against. A term
   * plan has an endDate; an ONGOING plan can still carry a hard
   * commitment through its batches (EMCOR is open-ended but owes 125
   * locations by Oct 31). Without this the plan shape that most needs a
   * pace read was the one that never got one. Earliest open batch
   * target wins, because that is the next deadline that can be missed.
   */
  const governingTargetDate = useMemo(() => {
    const targets = (groups || [])
      .map((g) => g?.targetDate)
      .filter(Boolean)
      .map((d) => moment(d as string))
      .filter((m) => m.isValid() && m.isAfter(moment()));
    if (targets.length === 0) return null;
    return moment.min(targets).toISOString();
  }, [groups]);

  // N5 — pins narrowed by the status filter. "overdue" keeps any point
  // flagged overdue regardless of its base status; a status value keeps
  // only that bucket; "all" keeps everything. Batch scoping is done
  // server-side via the fetch's groupId for the roster points; the
  // WO-derived points appended below re-apply it themselves.
  /**
   * MAP STATUS, DERIVED FROM THE PLAN'S WORK ORDERS (Session 69).
   *
   * The map endpoint's own bucket SQL disagreed with the WO table in three
   * ways, all reproduced on live EMCOR/LCG data:
   *   1. It walks the PLAN ROSTER (maintenance_plan_lcp) only, so a WO at a
   *      site with no plan-roster row had no pin and was counted nowhere.
   *      12 of the 50 LCG work orders were invisible this way (11 of them
   *      are on the BATCH roster but were never added to the PLAN roster).
   *   2. Its "open" was `status IN (received, matched)`, so every converted
   *      case dropped out of Open, while the WO table (N4) counts a case
   *      open until it is case-closed.
   *   3. A site whose only WO was converted, with no project in steps 1-4
   *      and no service_event, fell through every branch to "no_wo" and
   *      painted grey - live work reading as "No work order". 3 sites.
   *
   * Fix: the endpoint stays the source of coordinates, batch tags and
   * cadence-overdue, but the STATUS is recomputed here from
   * `lcpWorkOrders` - the same array the WO table reads, already carrying
   * `caseClosed`. The two surfaces now cannot disagree, by construction
   * (same rule as the shared `workOrderNextAction` resolver).
   *
   * A location with no WO in that array keeps the server's bucket, which
   * is the branch that correctly separates serviced history from dormant.
   */
  const derivedMapPoints = useMemo<PlanMapPoint[]>(() => {
    const base = mapPoints || [];
    const wos = lcpWorkOrders || [];

    // WOs keyed by LOCATION id: a WO hangs off an LCP, a pin off a
    // location, and two LCPs can share one location.
    const byLocation: Record<string, any[]> = {};
    for (const wo of wos) {
      const locId = (wo as any)?.lcp?.location?.id ?? null;
      if (!locId) continue;
      (byLocation[locId] = byLocation[locId] || []).push(wo);
    }

    // N4's definition, verbatim: a case is live until it is case-closed.
    const isLive = (wo: any) => wo?.status !== "rejected" && wo?.caseClosed !== true;
    const isPreRfq = (wo: any) => wo?.status === "received" || wo?.status === "matched";

    const bucketFor = (
      rows: any[],
    ): { mapStatus: PlanMapStatus; openWoCount: number; activeWorkOrderId: string | null } => {
      const live = rows.filter(isLive);
      const preRfq = live.filter(isPreRfq);
      const lead = preRfq[0] || live[0] || rows[0] || null;
      let mapStatus: PlanMapStatus;
      if (preRfq.length > 0) mapStatus = "open";
      else if (live.length > 0) mapStatus = "in_production";
      else mapStatus = "closed";
      return { mapStatus, openWoCount: live.length, activeWorkOrderId: lead?.id ?? null };
    };

    const seen = new Set<string>();
    const points: PlanMapPoint[] = base.map((p) => {
      const locId = p.locationId || "";
      if (locId) seen.add(locId);
      const rows = locId ? byLocation[locId] : null;
      if (!rows || rows.length === 0) return p;
      const d = bucketFor(rows);
      return {
        ...p,
        mapStatus: d.mapStatus,
        openWoCount: d.openWoCount,
        activeWorkOrderId: d.activeWorkOrderId ?? p.activeWorkOrderId ?? null,
      };
    });

    // Defect 1 - pins for plan WOs whose site is missing from the plan
    // roster. Coordinates come off the WO's own joined location, so no
    // extra request. When a batch is scoped, only WOs in that batch are
    // added, mirroring the server-side scoping of the roster points.
    for (const locId of Object.keys(byLocation)) {
      if (seen.has(locId)) continue;
      const rows = byLocation[locId].filter((wo: any) =>
        scopeGroupId ? (wo?.group?.id ?? wo?.groupId ?? null) === scopeGroupId : true,
      );
      if (rows.length === 0) continue;
      const loc = rows[0]?.lcp?.location;
      const lat = loc?.latitude != null ? Number(loc.latitude) : null;
      const lng = loc?.longitude != null ? Number(loc.longitude) : null;
      const d = bucketFor(rows);
      points.push({
        locationId: locId,
        name: rows[0]?.lcp?.name ?? loc?.name ?? null,
        address: loc?.address ?? null,
        latitude: lat != null && Number.isFinite(lat) ? lat : null,
        longitude: lng != null && Number.isFinite(lng) ? lng : null,
        batchId: rows[0]?.group?.id ?? null,
        batchName: rows[0]?.group?.name ?? null,
        mapStatus: d.mapStatus,
        overdue: false,
        openWoCount: d.openWoCount,
        activeWorkOrderId: d.activeWorkOrderId,
      });
    }

    return points;
  }, [mapPoints, lcpWorkOrders, scopeGroupId]);

  const filteredMapPoints = useMemo<PlanMapPoint[]>(() => {
    const all = derivedMapPoints;
    if (mapStatusFilter === "all") return all;
    if (mapStatusFilter === "overdue") return all.filter((p) => p.overdue);
    return all.filter((p) => p.mapStatus === mapStatusFilter);
  }, [derivedMapPoints, mapStatusFilter]);

  // Counts per bucket for the filter chips, from the unfiltered set so the
  // numbers don't move as you filter by them.
  const mapStatusCounts = useMemo(() => {
    const all = derivedMapPoints;
    return {
      all: all.length,
      no_wo: all.filter((p) => p.mapStatus === "no_wo").length,
      open: all.filter((p) => p.mapStatus === "open").length,
      in_production: all.filter((p) => p.mapStatus === "in_production").length,
      closed: all.filter((p) => p.mapStatus === "closed").length,
      overdue: all.filter((p) => p.overdue).length,
    };
  }, [derivedMapPoints]);

  const [showImport, setShowImport] = useState(false);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [showNewWorkOrder, setShowNewWorkOrder] = useState(false);
  // Attach existing work (2026-10-15): the client's RFQs/quotes/projects
  // from before the plan, brought under it by id.
  const [showAttachWork, setShowAttachWork] = useState(false);
  // WO CSV import (Session 67). Distinct from showImport, which imports
  // SITES onto the plan — this imports WORK ORDERS from a Corrigo CSV.
  const [showImportWos, setShowImportWos] = useState(false);
  const [showNewDirectQuote, setShowNewDirectQuote] = useState(false);
  // Set when a create action is triggered from a specific Location
  // row's expanded panel, so the modal opens with that Location chosen.
  const [preselectedLcpId, setPreselectedLcpId] = useState<string | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  // Products — to resolve cadence rows' product-id `type` to a name in
  // the config panel (LIVE_VS_MOCKUP_GAP P5). Loaded once on mount.
  const [products, setProducts] = useState<Array<{ id: string; name: string }> | null>(null);

  useEffect(() => {
    getInstantEstimateProducts()
      .then((rows: any[]) => setProducts(Array.isArray(rows) ? rows : []))
      .catch(() => setProducts([]));
  }, []);

  // N5 — lazy map fetch. Fires only once the panel is opened, and refetches
  // when the batch scope changes while it's open. Cached in state, so
  // collapsing/reopening does not refetch (unless the scope moved).
  useEffect(() => {
    if (!showMap) return;
    let cancelled = false;
    setMapLoading(true);
    getPlanLocationMap({ data: { id, groupId: scopeGroupId || undefined } })
      .then((rows: any[]) => {
        if (!cancelled) setMapPoints(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setMapPoints([]);
      })
      .finally(() => {
        if (!cancelled) setMapLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showMap, id, scopeGroupId]);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Plan";
    load();
    // eslint-disable-next-line
  }, [id]);

  const load = async () => {
    setLoading(true);
    // Parallel-fetch everything the page renders — five endpoints, one
    // Promise.all. The stat cards and sites list feed the batches
    // section's progress math, so both must land before we render.
    // SCALE: `getPlanEnrichedSites` is UNPAGED — it returns every
    // Location on the plan (1,084 for EMCOR/LCG). It is not used by the
    // roster table below, which pages itself server-side; its only
    // consumers are the site pickers inside the New Work Order / New
    // Quote modals. Fetching it on every page open paid for 1,084 rows
    // to render a page that mostly never opens those modals, so it is
    // now loaded on demand (see `ensureSitesLoaded`).
    const [planRes, statsRes, actRes, grpRes] = await Promise.all([
      getServicePlan({ data: { id } }),
      getPlanStats({ data: { id } }),
      getPlanActivity({ data: { id } }),
      getPlanGroups({ data: { planId: id } }),
    ]);

    setPlan(planRes?.id ? planRes : null);
    setStats(statsRes || null);
    setRosterVersion((v) => v + 1);
    setActivity(Array.isArray(actRes) ? actRes : []);
    setGroups(Array.isArray(grpRes) ? grpRes : []);
    setLoading(false);

    // WOs load in parallel-ish but don't gate the first paint of the
    // rest of the page. Only fire for work-order-fed plans.
    //
    // SCALE: this used to be TWO unbounded fetches of the same rows —
    // one lifecycle-filtered for the section, one unfiltered for the
    // per-Location row expansions. At LCG volume that is the same large
    // object graph pulled twice through Lambda on every page open. One
    // fetch now; the lifecycle slice is derived from it in the section,
    // which was already filtering client-side for the cases the server
    // filter cannot express.
    if (planRes?.intakeStyle === "work_order_fed") {
      loadPlanWos(planRes.id);
    } else {
      setPlanWorkOrders([]);
      setPlanWosLoading(false);
      setLcpWorkOrders([]);
    }
  };

  // The plan's work orders — ONE unfiltered read, shared by the WO
  // section (which narrows it) and the Locations row expansions.
  const loadPlanWos = async (planId: string) => {
    setPlanWosLoading(true);
    const res = await getWorkOrders({ query: { servicePlanId: planId } });
    const rows = Array.isArray(res) ? res : [];
    setPlanWorkOrders(rows);
    setLcpWorkOrders(rows);
    setPlanWosLoading(false);
  };

  const reloadWorkOrders = async () => {
    if (!plan?.id && !id) return;
    const pid = (plan?.id as string) || (id as string);
    await loadPlanWos(pid);
  };

  // Lifecycle is now a client-side narrowing over the single fetch, so
  // changing it costs no request.
  const onWoLifecycleChange = (v: string) => {
    setWoLifecycle(v);
  };

  /**
   * Load the plan's full Location list, once, only when something
   * actually needs it (the site pickers in the create modals). Returns
   * immediately if already loaded or in flight.
   */
  const sitesLoadedRef = React.useRef(false);
  const [sitesLoading, setSitesLoading] = useState(false);
  const ensureSitesLoaded = async () => {
    if (sitesLoadedRef.current || !id) return;
    sitesLoadedRef.current = true;
    setSitesLoading(true);
    const sitesRes = await getPlanEnrichedSites({ data: { id } });
    setEnrichedSites(Array.isArray(sitesRes) ? sitesRes : []);
    setSitesLoading(false);
  };

  const openNewWorkOrder = (lcpId?: string | null) => {
    setPreselectedLcpId(lcpId ?? null);
    ensureSitesLoaded();
    setShowNewWorkOrder(true);
  };

  const openNewDirectQuote = (lcpId?: string | null) => {
    setPreselectedLcpId(lcpId ?? null);
    ensureSitesLoaded();
    setShowNewDirectQuote(true);
  };

  /**
   * Refresh after a roster write, WITHOUT the full-page spinner.
   * `load()` flips `loading` and unmounts the whole tree, which would
   * close the open roster panel the user is working in — allocating
   * five Locations in a row would collapse the panel five times. This
   * refetches only what a roster write can change and leaves the
   * section mounted.
   */
  const refreshAfterRosterChange = async () => {
    const [actRes, statsRes] = await Promise.all([
      getPlanActivity({ data: { id } }),
      getPlanStats({ data: { id } }),
    ]);
    setActivity(Array.isArray(actRes) ? actRes : []);
    setRosterVersion((v) => v + 1);
    if (statsRes) setStats(statsRes);
    // Only refresh the (unpaged) site list if something already pulled
    // it — otherwise a roster write would reintroduce the 1,084-row
    // fetch this page just stopped making.
    if (sitesLoadedRef.current) {
      const sitesRes = await getPlanEnrichedSites({ data: { id } });
      setEnrichedSites(Array.isArray(sitesRes) ? sitesRes : []);
    }
    // A WO state change (match / generate-RFQ) also shifts the WO
    // lists and the sites' open-WO counts, so refresh those too.
    await reloadWorkOrders();
  };

  const submitClose = async () => {
    setClosing(true);
    const res = await closeServicePlan({ data: { id } });
    setClosing(false);
    setCloseModalOpen(false);
    if (res?.id) {
      toast.add("Plan closed.", "var(--theme-ui-colors-success)");
      setPlan(res);
    } else {
      toast.add(res?.message || "Error closing plan.", "var(--theme-ui-colors-danger)");
    }
  };

  if (loading) {
    return (
      <>
        <Header></Header>
        <Page title="Plan">
          <Box sx={{ textAlign: "center", paddingY: "40px" }}>
            <Spinner />
          </Box>
        </Page>
      </>
    );
  }

  if (!plan) {
    return (
      <>
        <Header></Header>
        <Page title="Plan not found">
          <P>This plan doesn&apos;t exist or you don&apos;t have access to it.</P>
        </Page>
      </>
    );
  }

  const isTermBased = !!plan.endDate;
  // OPERATING_MODEL.md — intake style gates which surfaces render.
  // Defaults to quote_fed for plans created before the column existed.
  const isWorkOrderFed = plan.intakeStyle === "work_order_fed";
  // SESSION 45 GAP-28 — read the backend-computed effectiveStatus if
  // present, fall back to raw status for older payloads. `closing`
  // renders as a distinct amber badge and swaps the header sub-line to
  // announce the deadline lapse.
  const effectiveStatus: "open" | "closing" | "closed" =
    plan.effectiveStatus ?? plan.status ?? "open";

  // Recurring Work (design/CADENCE_SCHEDULER.md) — the plan has a live
  // schedule when any product row carries a structured cadenceRule. Gates
  // the right-rail Recurring Work history panel.
  const hasRecurringWork =
    Array.isArray(plan.serviceTypesConfig) &&
    plan.serviceTypesConfig.some((s: any) => s?.cadenceRule && s.cadenceRule.mode);

  // P-m triage links — each figure in "Needs attention today" filters the
  // relevant section and scrolls to it, so the line is a way in, not a
  // read-only summary (mockup 28).
  //
  // Session 76: the target section may now live on a different TAB, so
  // each link switches tab first. The scroll is deferred a frame because
  // the target does not exist in the DOM until the tab has rendered —
  // calling scrollIntoView synchronously would silently do nothing.
  const scrollToRef = (ref: React.RefObject<HTMLDivElement | null>) =>
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const goToSection = (
    target: PlanTab,
    ref: React.RefObject<HTMLDivElement | null>,
  ) => {
    selectTab(target);
    requestAnimationFrame(() => scrollToRef(ref));
  };
  const triageToUnmatched = () => {
    onWoLifecycleChange("needs_match");
    goToSection("work", woSectionRef);
  };
  const triageToPastSla = () => {
    onWoLifecycleChange("open");
    goToSection("work", woSectionRef);
  };
  const triageToOverdueNoWo = () => {
    setRosterView("overdue_no_wo");
    goToSection("sites", rosterSectionRef);
  };

  return (
    <>
      <Header></Header>
      <Page
        backAction={
          <Link
            variant="back"
            href="/maintenance"
            sx={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <BackArrowIcon />
            Back to Maintenance
          </Link>
        }
        $noPadding={true}
      >
        {/* SESSION 46 — one centered container bounds the header row
         * AND the two-column body, so the title, action buttons, main
         * column, and rail all share edges instead of the buttons
         * flying to the viewport edge over a dead right gutter. */}
        <Box sx={{ maxWidth: "1200px", marginX: "auto", width: "100%" }}>
          {/* ── Header row ── */}
          <Flex
            sx={{
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginTop: "16px",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <Box sx={{ flex: 1, minWidth: "260px" }}>
              <Flex sx={{ alignItems: "center", gap: "10px" }}>
                <H5 fontFamily="Poppins" fontWeight={600} margin={0} padding={0} fontSize={"20px"}>
                  {plan.name}
                </H5>
                <Badge
                  as="span"
                  variant={
                    effectiveStatus === "closing"
                      ? "warning"
                      : effectiveStatus === "open"
                      ? "success"
                      : "info"
                  }
                >
                  {effectiveStatus === "open"
                    ? "Open"
                    : effectiveStatus === "closing"
                    ? "Closing"
                    : "Closed"}
                </Badge>
              </Flex>
              <Subtext
                fontSize={"13px"}
                fontFamily={"Poppins"}
                marginTop={"6px"}
                marginBottom={0}
              >
                <Link href={`/clients/${plan.client?.id}`}>{plan.client?.name}</Link>
                {" · "}
                {isWorkOrderFed ? "Work-order fed" : "Quote fed"}
                {" · "}
                {isTermBased ? (
                  <>
                    Term-based · target {moment(plan.endDate).format("MMM D, YYYY")}
                    {plan.committedSiteCount ? ` · ${plan.committedSiteCount} sites committed` : ""}
                  </>
                ) : (
                  "Ongoing"
                )}
              </Subtext>
              {effectiveStatus === "closing" && (
                <Box
                  sx={{
                    marginTop: "10px",
                    padding: "10px 12px",
                    background: "#FFF8E1",
                    border: "1px solid #E7CC7A",
                    borderRadius: "6px",
                    maxWidth: "640px",
                  }}
                >
                  <Subtext
                    fontSize={"12px"}
                    fontFamily={"Poppins"}
                    margin={0}
                    color="#7A5A00"
                  >
                    This plan is past its target end date. Existing Work Orders continue; new Locations are refused until an admin extends the end date or switches to ongoing.
                  </Subtext>
                </Box>
              )}
              {plan.notes && (
                <P
                  fontSize={"13px"}
                  color={themevals.colors.dkgrey}
                  marginTop={"10px"}
                  marginBottom={0}
                >
                  {plan.notes}
                </P>
              )}
            </Box>
            {isAdmin && (
              <Flex sx={{ gap: "10px", flexWrap: "wrap", alignItems: "flex-start" }}>
                {/* SESSION 45 DRIFT-1 / P-f. Mockup 13 drew two primary
                 * controls; the shipped header carried five, then three.
                 * Now two: the work-creation button and the overflow.
                 * `Add Location` moved into the Locations section header
                 * (mockup 26) where it belongs — it acts on the roster,
                 * so it lives next to the roster instead of competing
                 * with the page's primary action.
                 *
                 * OPERATING_MODEL.md — the primary work-creation button
                 * is intake-style gated. Work-order-fed plans raise a
                 * Work Order (EMCOR/Corrigo). Quote-fed plans issue a
                 * quote directly at a location; they never see a work
                 * order. */}
                {isWorkOrderFed && (
                  <BasicButton
                    type="button"
                    scheme="primary"
                    onClick={() => openNewWorkOrder()}
                  >
                    + New Work Order
                  </BasicButton>
                )}
                {isWorkOrderFed && (
                  <BasicButton
                    type="button"
                    scheme="secondary"
                    outline
                    onClick={() => setShowImportWos(true)}
                  >
                    Import Work Orders
                  </BasicButton>
                )}
                {!isWorkOrderFed && (
                  <BasicButton
                    type="button"
                    scheme="primary"
                    onClick={() => openNewDirectQuote()}
                  >
                    + New Quote
                  </BasicButton>
                )}
                <PlanManageMenu
                  onEdit={() => setShowEdit(true)}
                  onImport={() => setShowImport(true)}
                  onAddLocation={() => setShowAddLocation(true)}
                  onAttachWork={() => setShowAttachWork(true)}
                  onClose={
                    plan.status === "open" ? () => setCloseModalOpen(true) : null
                  }
                />
              </Flex>
            )}
          </Flex>

          {/* ── Mockup 26 composition (P-h, decided Session 55).
           * The roster is the object of this page at 1,084 rows, so the
           * work column runs FULL WIDTH and the reference material
           * (config, recurring work, activity) drops to a three-up band
           * below it. The previous right rail cost the table a third of
           * its width to hold things you consult occasionally. ── */}
          <Box sx={{ marginTop: "20px" }}>
              {/* Session 56 (mockup 29) — top band. PLAN TOTALS on the
                  left (plan-level; these four do NOT change with the
                  batch scope) and NEEDS ATTENTION on the right, both
                  bounded titled cards so the page reads dense like the
                  work-order case file instead of thin. */}
              <Flex
                sx={{
                  gap: "16px",
                  flexWrap: "wrap",
                  alignItems: "stretch",
                  marginBottom: "20px",
                }}
              >
                <Box sx={{ flex: "2 1 460px", minWidth: "300px" }}>
                  <Box sx={{ ...mntCard, padding: "14px 16px", height: "100%" }}>
                    <PlanSectionHeader
                      label="Plan totals"
                      hint="whole plan, unaffected by the batch scope"
                      sx={{ marginBottom: "10px" }}
                    />
                    <PlanStatCards
                      stats={stats}
                      committedSiteCount={plan.committedSiteCount}
                      endDate={plan.endDate}
                      targetDate={plan.endDate ?? governingTargetDate}
                      termStart={plan.createdAt}
                      loading={false}
                    />
                    {/*
                      PLAN_FINANCIAL_CARDS.md - the money row. Session 76:
                      moved OUT of the Plan totals card. Eight cards in
                      one band stopped being a summary, and the margin
                      figure - the single most consequential number here -
                      was card eight of eight. It now sits in its own
                      strip below, where four money figures read as one
                      answer to one question.
                      Operator surface only: this carries cost and margin,
                      and PlanClientDetail must never render it.
                    */}
                  </Box>
                </Box>
                {isWorkOrderFed &&
                  (triage.unmatched > 0 ||
                    triage.pastSla > 0 ||
                    triage.overdueNoWo > 0) && (
                    <Box sx={{ flex: "1 1 260px", minWidth: "240px" }}>
                      <Box
                        sx={{
                          background: mnt.amberPill,
                          border: `1px solid ${mnt.amberStroke}`,
                          borderRadius: "8px",
                          padding: "12px 16px",
                          height: "100%",
                        }}
                      >
                        <Span
                          fontFamily={"Poppins"}
                          fontSize={"12px"}
                          fontWeight={600}
                          sx={{ color: mnt.amber, display: "block", marginBottom: "8px" }}
                        >
                          Needs attention today
                        </Span>
                        {triage.unmatched > 0 && (
                          <TriageFigure onClick={triageToUnmatched} color={mnt.red}>
                            {triage.unmatched} work order{triage.unmatched === 1 ? "" : "s"} unmatched
                          </TriageFigure>
                        )}
                        {triage.pastSla > 0 && (
                          <TriageFigure onClick={triageToPastSla} color={mnt.red}>
                            {triage.pastSla} overdue
                          </TriageFigure>
                        )}
                        {!scopeGroupId && triage.overdueNoWo > 0 && (
                          <TriageFigure onClick={triageToOverdueNoWo} color={mnt.amber} last>
                            {triage.overdueNoWoExact ? "" : "about "}
                            {triage.overdueNoWo} overdue location{triage.overdueNoWo === 1 ? "" : "s"} with no work order raised
                          </TriageFigure>
                        )}
                      </Box>
                    </Box>
                  )}
              </Flex>

              {/* Tab bar. Plan totals and Needs attention stay ABOVE it —
                  they are plan-level and are the two things allowed to
                  interrupt whichever tab you are working in. */}
              <PlanTabBar
                active={tab}
                onSelect={selectTab}
                counts={{
                  work: Array.isArray(planWorkOrders) ? planWorkOrders.length : undefined,
                  sites: Number(rosterCounts?.all ?? 0) || undefined,
                }}
              />

              {/* Money, in its own tab. Session 76: four financial
                  figures are one question asked at one time, usually by
                  Jack and not by whoever is working the queue. */}
              {tab === "financials" && (
                <Box sx={{ marginBottom: "20px" }}>
                  <PlanFinancialCards planId={plan.id} />
                </Box>
              )}

              {tab === "work" && (
              <>
              <WorkOrderGroupsSection
                planId={plan.id}
                groups={groups}
                canEdit={isAdmin && isWorkOrderFed}
                intakeStyle={plan.intakeStyle}
                woStatsByGroup={woStatsByGroup}
                stageDistByGroup={stageDistByGroup}
                selectedGroupId={scopeGroupId}
                onSelectGroup={setScopeGroupId}
                onGroupCreated={(g) => {
                  setGroups((prev) => [g, ...prev]);
                  // Refresh activity so the new group shows up.
                  getPlanActivity({ data: { id } }).then((r) => setActivity(Array.isArray(r) ? r : []));
                }}
                onGroupEdited={(g) => {
                  setGroups((prev) => prev.map((x) => (x.id === g.id ? g : x)));
                }}
              />

              {/* Session 56 (mockup 29) — scope reads as STATE, not a
                  dark control bar. The selected batch chip above is
                  already filled; this quiet tag confirms it and clears
                  it. The stat cards above stay plan-wide. */}
              {scopeGroupId && (
                <Flex
                  sx={{ alignItems: "center", gap: "10px", marginBottom: "14px" }}
                >
                  <Flex
                    sx={{
                      alignItems: "center",
                      gap: "8px",
                      background: "#EDEBE4",
                      border: `1px solid ${mnt.divider}`,
                      borderRadius: "12px",
                      padding: "3px 10px",
                    }}
                  >
                    <Span
                      fontFamily={"Poppins"}
                      fontSize={"11px"}
                      sx={{ color: mnt.textMuted }}
                    >
                      Scoped to {groups.find((g) => g.id === scopeGroupId)?.name || "batch"}
                    </Span>
                    <Box
                      as="button"
                      onClick={() => setScopeGroupId(null)}
                      sx={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        color: mnt.textMuted,
                        fontSize: "12px",
                        lineHeight: 1,
                      }}
                    >
                      ✕
                    </Box>
                  </Flex>
                  <Span
                    fontFamily={"Poppins"}
                    fontSize={"10.5px"}
                    sx={{ color: mnt.textMuted }}
                  >
                    work orders and locations below show this batch only
                  </Span>
                </Flex>
              )}
              </>
              )}

              {/* Locations map. Session 76: on the LOCATIONS tab, above
                  the roster — Jack's call, after trying it as its own tab.
                  It is a good visual and it belongs with the sites it
                  plots, but it does not carry a job of its own, so a tab
                  overstated it. Above the plan tiles was the other option
                  and was rejected: always-rendered, it would put ~500px
                  between the header and the work queue on the default tab.
                  Expanded by default here; the Hide toggle collapses it
                  without leaving the tab, and the fetch stays gated on
                  that state so the other tabs never pay for it. Batch
                  scope follows the page's selected batch chip. */}
              {tab === "sites" && (
              <Box sx={{ marginBottom: "14px" }}>
                <Flex
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: showMap ? "10px" : 0,
                  }}
                >
                  <Box sx={{ ...mntType.labelSm, textTransform: "uppercase" }}>
                    Map{scopeGroupId ? " · scoped to selected batch" : ""}
                  </Box>
                  <Box
                    as="button"
                    onClick={() => setShowMap((v) => !v)}
                    sx={{
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: mnt.link,
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {showMap ? "Hide map" : "Show map"}
                  </Box>
                </Flex>

                {showMap && (
                  <Box>
                    {/* Status filter chips — counts from the unfiltered set.
                        These count SITES, not pins. A site with no
                        latitude/longitude is counted here but cannot be
                        drawn, which is why a chip's number is routinely
                        higher than the pins on the map. The line below
                        states that gap outright rather than leaving it to
                        the small "N not mappable" note inside the map. */}
                    <Flex sx={{ gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
                      {([
                        ["all", `All ${mapStatusCounts.all}`],
                        ["no_wo", `No WO ${mapStatusCounts.no_wo}`],
                        ["open", `Open ${mapStatusCounts.open}`],
                        ["in_production", `In production ${mapStatusCounts.in_production}`],
                        ["closed", `Closed ${mapStatusCounts.closed}`],
                        ["overdue", `Overdue ${mapStatusCounts.overdue}`],
                      ] as Array<[PlanMapStatus | "all" | "overdue", string]>).map(
                        ([key, label]) => {
                          const active = mapStatusFilter === key;
                          return (
                            <Box
                              as="button"
                              key={key}
                              onClick={() => setMapStatusFilter(key)}
                              sx={{
                                fontFamily: "Poppins",
                                fontSize: "12px",
                                padding: "5px 12px",
                                borderRadius: "20px",
                                cursor: "pointer",
                                border: `1px solid ${active ? mnt.ink : mnt.divider}`,
                                background: active ? mnt.ink : mnt.cardBg,
                                color: active ? "#fff" : mnt.ink,
                              }}
                            >
                              {label}
                            </Box>
                          );
                        },
                      )}
                    </Flex>

                    {/* Session 69 - state the pin/site gap plainly. The
                        chips count sites in each bucket; only sites with
                        coordinates can be drawn. On LCG 110 of 150 sites
                        have no lat/lng, so "All 150" renders 40 pins and
                        reads as a broken filter unless this is said. */}
                    {(() => {
                      const shown = filteredMapPoints.filter(
                        (p) => p.latitude != null && p.longitude != null,
                      ).length;
                      const missing = filteredMapPoints.length - shown;
                      if (missing <= 0) return null;
                      return (
                        <Subtext
                          fontSize={"11.5px"}
                          fontFamily={"Poppins"}
                          margin={0}
                          color={mnt.textMuted}
                          sx={{ marginBottom: "8px" }}
                        >
                          Showing {shown} of {filteredMapPoints.length} sites on the map.{" "}
                          {missing} have no coordinates yet and cannot be pinned.
                        </Subtext>
                      );
                    })()}

                    {mapLoading && mapPoints === null ? (
                      <Box
                        sx={{
                          height: "440px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: mnt.bgSubtle,
                          borderRadius: "8px",
                          color: mnt.textMuted,
                          fontFamily: "Poppins",
                          fontSize: "13px",
                        }}
                      >
                        Loading map…
                      </Box>
                    ) : (
                      <PlanLocationsMap
                        points={filteredMapPoints}
                        selectedId={selectedMapLocationId}
                        onSelect={setSelectedMapLocationId}
                        onOpenLocation={(locId: string) => navigate(`/locations/${locId}`)}
                        onOpenWorkOrder={(woId: string) => navigate(`/maintenance/work-orders/${woId}`)}
                      />
                    )}
                  </Box>
                )}
              </Box>
              )}

              {/* Session 48 — plan-scoped Work Order list. The plan is
               * the work surface: see, open, and act on every WO on
               * this plan without leaving for the global queue.
               * Work-order-fed only (renders nothing for quote-fed).
               * A state change here (match / generate-RFQ) refreshes
               * the sites table + stats + activity so the Locations
               * rows and stat cards stay truthful.
               * Session 76: the WORK tab — this is the default view and
               * the reason the page exists. */}
              {tab === "work" && (
              <div ref={woSectionRef} style={{ scrollMarginTop: "90px" }}>
              <PlanWorkOrdersSection
                planId={plan.id}
                intakeStyle={plan.intakeStyle}
                canEdit={isAdmin && isWorkOrderFed}
                onChanged={refreshAfterRosterChange}
                workOrders={planWorkOrders}
                allWorkOrders={lcpWorkOrders}
                scopeGroupId={scopeGroupId}
                loading={planWosLoading}
                lifecycleFilter={woLifecycle}
                onLifecycleChange={onWoLifecycleChange}
                onReload={reloadWorkOrders}
              />
              </div>
              )}

              {/* Session 76: the SITES tab — roster, batching allocation
                  and the map, the three jobs that are about which sites
                  are in the plan rather than what work is outstanding. */}
              {tab === "sites" && (
              <div ref={rosterSectionRef} style={{ scrollMarginTop: "90px" }}>

              <PlanLocationsTable
                planId={plan.id}
                groups={groups}
                view={rosterView}
                onViewChange={setRosterView}
                intakeStyle={plan.intakeStyle}
                canAllocate={isAdmin && isWorkOrderFed}
                refreshKey={rosterVersion}
                scopeGroupId={scopeGroupId}
                onCounts={setRosterCounts}
                woByLcp={woByLcp}
                onMatchWo={isAdmin && isWorkOrderFed ? (w: any) => setRosterMatchTarget(w) : undefined}
                onOpenWo={(woId: string) => navigate(`/maintenance/work-orders/${woId}`)}
                onAddLocation={isAdmin ? () => setShowAddLocation(true) : undefined}
                onAllocate={async (lcpIds: string[], groupId: string) => {
                  const res = await addToGroupRoster({
                    data: { planId: plan.id, groupId, lcpIds },
                  });
                  if (typeof res?.added === "number") {
                    const skipped = res.alreadyPresent
                      ? ` (${res.alreadyPresent} already on this batch)`
                      : "";
                    toast.add(
                      `${res.added} Location${res.added === 1 ? "" : "s"} added to batch${skipped}.`,
                      "var(--theme-ui-colors-success)",
                    );
                    await refreshAfterRosterChange();
                    return true;
                  }
                  toast.add(
                    res?.message || "Error adding Locations to the batch.",
                    "var(--theme-ui-colors-danger)",
                  );
                  return false;
                }}
                onNewWorkOrder={
                  isAdmin && isWorkOrderFed
                    ? (lcpId: string) => openNewWorkOrder(lcpId)
                    : undefined
                }
                onNewQuote={
                  isAdmin && !isWorkOrderFed
                    ? (lcpId: string) => openNewDirectQuote(lcpId)
                    : undefined
                }
                onRemoveFromBatch={
                  isAdmin && isWorkOrderFed
                    ? async (lcpId: string, groupId: string) => {
                        const res = await removeFromGroupRoster({
                          data: { planId: plan.id, groupId, lcpId },
                        });
                        if (res?.success) {
                          toast.add(
                            "Location removed from batch.",
                            "var(--theme-ui-colors-success)",
                          );
                          await refreshAfterRosterChange();
                          return true;
                        }
                        toast.add(
                          res?.message ||
                            "Could not remove this Location from the batch.",
                          "var(--theme-ui-colors-danger)",
                        );
                        return false;
                      }
                    : undefined
                }
              />
              </div>
              )}
          </Box>

          {/* ── Reference band. Plan config moved ABOVE the batches
           * (Session 60); what remains here is recurring-work history and
           * the activity feed — things you consult, not watch. Both render
           * FULL-WIDTH now so they use the page height better under the
           * new 10/page location pagination. The old "Plan reference"
           * section header was dropped once config left — the panels below
           * carry their own headings, so it was redundant. A divider rule
           * still closes the page. (Jack, Session 60.) ── */}
          {tab === "reference" && (
          <Box sx={{ marginTop: "24px" }}>
            <Box
              sx={{ borderTop: `0.5px solid ${mnt.divider}`, marginBottom: "18px" }}
            />
            {/* Plan config — moved DOWN here in Session 76, reversing the
                Session 60 placement. The reasoning then was that config is
                reference you consult before working the batches. In
                practice nobody consults it: it is a five-column read-only
                panel that never changes between visits, and at the top of
                the page it pushed the work queue below the fold on a page
                whose job is working that queue. It belongs with the other
                reference panels (recurring work, activity). Editing is
                unchanged — same Edit affordance, same modal. */}
            <Box sx={{ marginBottom: "16px" }}>
              <PlanConfigPanel
                plan={plan}
                canEdit={isAdmin}
                onEdit={() => setShowEdit(true)}
                products={products}
                layout="horizontal"
              />
            </Box>
            {/* Recurring Work history — only when a schedule is
             * configured (any serviceTypesConfig row carries a live
             * cadenceRule). Admins get the Run-now trigger. Full-width row
             * above the activity feed. */}
            {hasRecurringWork && (
              <Box sx={{ marginBottom: "16px" }}>
                <RecurringWorkPanel planId={plan.id} canRun={isAdmin} />
              </Box>
            )}
            <Box>
              <RecentActivityPanel events={activity} loading={false} />
            </Box>
          </Box>
          )}
        </Box>
      </Page>

      {showImport && (
        <ImportSitesModal
          planId={plan.id}
          planName={plan.name}
          onClose={() => setShowImport(false)}
          onImported={() => {
            setShowImport(false);
            load();
          }}
        />
      )}

      {showAddLocation && (
        <AddLocationToPlanModal
          planId={plan.id}
          planName={plan.name}
          clientId={plan.client?.id}
          onClose={() => setShowAddLocation(false)}
          onAdded={() => {
            setShowAddLocation(false);
            load();
          }}
        />
      )}

      {showNewWorkOrder && (
        <NewWorkOrderModal
          planId={plan.id}
          planName={plan.name}
          clientId={plan.client?.id}
          sites={enrichedSites}
          sitesLoading={sitesLoading}
          preselectedLcpId={preselectedLcpId || undefined}
          planServiceTypes={plan.serviceTypesConfig}
          onClose={() => {
            setShowNewWorkOrder(false);
            setPreselectedLcpId(null);
          }}
          onCreated={() => {
            setShowNewWorkOrder(false);
            setPreselectedLcpId(null);
            load();
          }}
        />
      )}

      {showAttachWork && (
        <AttachExistingWorkModal
          mode="plan"
          planId={plan.id}
          planName={plan.name}
          onClose={() => setShowAttachWork(false)}
          onDone={() => {
            setShowAttachWork(false);
            load();
            refreshAfterRosterChange?.();
          }}
        />
      )}

      {showImportWos && (
        <WorkOrderImportModal
          servicePlanId={plan.id}
          groupId={scopeGroupId || undefined}
          planLabel={
            plan.name +
            (scopeGroupId
              ? ` \u00b7 ${groups.find((g) => g.id === scopeGroupId)?.name || "batch"}`
              : "")
          }
          onCancel={() => setShowImportWos(false)}
          onCommitted={() => {
            setShowImportWos(false);
            load();
          }}
        />
      )}

      {showNewDirectQuote && (
        <NewDirectQuoteModal
          planId={plan.id}
          planName={plan.name}
          sites={enrichedSites}
          sitesLoading={sitesLoading}
          preselectedLcpId={preselectedLcpId || undefined}
          onClose={() => {
            setShowNewDirectQuote(false);
            setPreselectedLcpId(null);
          }}
        />
      )}

      {rosterMatchTarget && (
        <MatchCandidatesModal
          workOrder={rosterMatchTarget}
          onClose={() => setRosterMatchTarget(null)}
          onMatched={async () => {
            setRosterMatchTarget(null);
            await refreshAfterRosterChange();
          }}
        />
      )}

      {showEdit && (
        <EditPlanModal
          plan={plan}
          onClose={() => setShowEdit(false)}
          onSaved={(updated) => {
            setShowEdit(false);
            setPlan(updated);
          }}
        />
      )}

      {closeModalOpen && (
        <BasicModal
          title="Close this Maintenance Plan?"
          cancel_click={() => setCloseModalOpen(false)}
          cancel_text="No, cancel"
          size="sm"
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          confirm_click={submitClose}
          confirm_text="Yes, close plan"
          submitting={closing}
        >
          <P>
            Are you sure you want to close{" "}
            <strong style={{ color: themevals.colors.black }}>{plan.name}</strong>?
            Sites stay attached for history, but the plan stops driving new activity.
          </P>
        </BasicModal>
      )}
    </>
  );
}

/**
 * SESSION 45 DRIFT-1 — overflow menu on the Plan Detail header. Holds
 * Edit plan (default action), Import Locations, and Close Plan. The
 * bare-bones popover is intentional — the codebase has no dropdown
 * primitive, and pulling one in for a 3-item menu is more surface than
 * the finding warrants. Click-outside-to-close is wired via a document
 * listener rather than a shared hook so nothing new is introduced.
 */
function PlanManageMenu({
  onEdit,
  onImport,
  onAddLocation,
  onAttachWork,
  onClose,
}: {
  onEdit: () => void;
  onImport: () => void;
  /** Bring the client's existing RFQs / quotes / projects under the plan. */
  onAttachWork: () => void;
  /** P-f — also offered here, not only in the Locations header, so the
   * action is still reachable from the top of the page. */
  onAddLocation: () => void;
  /** null hides the Close Plan option (plan is already closed). */
  onClose: (() => void) | null;
}) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <Box ref={ref} sx={{ position: "relative", display: "inline-block" }}>
      <BasicButton
        type="button"
        outline
        scheme="secondary"
        onClick={() => setOpen((v) => !v)}
      >
        Manage plan ▾
      </BasicButton>
      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: "200px",
            background: "white",
            border: "1px solid",
            borderColor: "inputOutline",
            borderRadius: "6px",
            boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.08)",
            zIndex: 20,
            padding: "6px 0",
          }}
        >
          <MenuItem
            label="Edit plan"
            onClick={() => {
              setOpen(false);
              onEdit();
            }}
          />
          <MenuItem
            label="Add Location"
            onClick={() => {
              setOpen(false);
              onAddLocation();
            }}
          />
          <MenuItem
            label="Import Locations"
            onClick={() => {
              setOpen(false);
              onImport();
            }}
          />
          <MenuItem
            label="Add existing work"
            onClick={() => {
              setOpen(false);
              onAttachWork();
            }}
          />
          {onClose && (
            <>
              <Box sx={{ borderTop: "1px solid", borderColor: "inputOutline", marginY: "4px" }} />
              <MenuItem
                label="Close Plan"
                danger
                onClick={() => {
                  setOpen(false);
                  onClose();
                }}
              />
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

function MenuItem({
  label,
  danger,
  onClick,
}: {
  label: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      as="button"
      onClick={onClick}
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        background: "transparent",
        border: "none",
        padding: "8px 14px",
        cursor: "pointer",
        fontFamily: "Poppins",
        fontSize: "13px",
        color: danger ? themevals.colors.danger : themevals.colors.black,
        "&:hover": { background: "#FAFAF8" },
      }}
    >
      {label}
    </Box>
  );
}

type PlanTab = "work" | "sites" | "financials" | "reference";

const PLAN_TABS: Array<{ key: PlanTab; label: string }> = [
  { key: "work", label: "Work" },
  { key: "sites", label: "Locations" },
  { key: "financials", label: "Financials" },
  { key: "reference", label: "Reference" },
];

/**
 * Plan detail tab bar. Counts sit in the label rather than a separate
 * badge element so a tab reads as one thing ("Work 40") instead of a
 * word with an ornament. A count of 0 renders nothing at all — a grey
 * zero is noise, and the absence is the same information.
 */
function PlanTabBar({
  active,
  onSelect,
  counts,
}: {
  active: PlanTab;
  onSelect: (t: PlanTab) => void;
  counts: Partial<Record<PlanTab, number>>;
}) {
  return (
    <Flex
      sx={{
        gap: "2px",
        borderBottom: `1px solid ${mnt.divider}`,
        marginBottom: "20px",
        overflowX: "auto",
      }}
    >
      {PLAN_TABS.map((t) => {
        const on = t.key === active;
        const n = counts[t.key];
        return (
          <Box
            as="button"
            key={t.key}
            onClick={() => onSelect(t.key)}
            sx={{
              background: "transparent",
              border: "none",
              borderBottom: on ? `2px solid ${mnt.ink}` : "2px solid transparent",
              padding: "9px 16px",
              marginBottom: "-1px",
              cursor: "pointer",
              fontFamily: "Poppins",
              fontSize: "13px",
              fontWeight: on ? 600 : 400,
              color: on ? mnt.ink : mnt.textMuted,
              whiteSpace: "nowrap",
              "&:hover": { color: mnt.ink },
            }}
          >
            {t.label}
            {n != null && n > 0 && (
              /* Box, not Span: the Span typography component takes margin
                 as props and drops sx margins, which rendered "Work35"
                 with no gap. */
              <Box
                as="span"
                sx={{
                  marginLeft: "7px",
                  fontFamily: "Poppins",
                  fontSize: "11px",
                  color: on ? mnt.ink : mnt.textFaint,
                }}
              >
                {n.toLocaleString()}
              </Box>
            )}
          </Box>
        );
      })}
    </Flex>
  );
}

/**
 * P-m — a single clickable figure in the "Needs attention today" card.
 * Reads as a link (color + hover underline) and, on click, filters the
 * relevant section and scrolls to it. Mockup 28: the triage line is a
 * way IN, not a read-only summary.
 */
function TriageFigure({
  onClick,
  color,
  last,
  children,
}: {
  onClick: () => void;
  color: string;
  last?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box
      as="button"
      onClick={onClick}
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        background: "transparent",
        border: "none",
        padding: 0,
        marginBottom: last ? 0 : "5px",
        fontFamily: "Poppins",
        fontSize: "11.5px",
        color,
        cursor: "pointer",
        "&:hover": { textDecoration: "underline" },
      }}
    >
      {children}
    </Box>
  );
}
